import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RiskScoreMethodology } from './riskEnums'

type RiskLevelDetail = { id: string; score: number }
type RiskMatrixDetail = { score: number }

// The subset of a summary risk level the reduction reads. Structural on purpose so both the request-grid
// rows and the CSV export's RiskLevelsSummary satisfy it without sharing a nominal type across the boundary.
export type SummaryRiskScores = {
  inherentRiskProbability: RiskLevelDetail
  inherentRiskImpact: RiskLevelDetail
  inherentRiskMatrix: RiskMatrixDetail | null
  residualRiskProbability: RiskLevelDetail
  residualRiskImpact: RiskLevelDetail
  residualRiskMatrix: RiskMatrixDetail | null
}

export type MaxRiskScore = {
  inherentRisk: { probability: RiskLevelDetail; impact: RiskLevelDetail; score: number }
  residualRisk: { probability: RiskLevelDetail; impact: RiskLevelDetail; score: number }
}

const getRiskScoreValue = (
  methodology: RiskScoreMethodology,
  { matrixScore, impactScore, probabilityScore }: { matrixScore?: number; impactScore: number; probabilityScore: number }
): number | undefined => (methodology === 'MULTIPLY' ? impactScore * probabilityScore : matrixScore)

const getMaxScoreForKind = (risks: SummaryRiskScores[], kind: 'inherent' | 'residual', methodology: RiskScoreMethodology) =>
  risks.reduce(
    (max, risk) => {
      const score = getRiskScoreValue(methodology, {
        matrixScore: risk[`${kind}RiskMatrix`]?.score,
        impactScore: risk[`${kind}RiskImpact`].score,
        probabilityScore: risk[`${kind}RiskProbability`].score,
      })
      return isDefined(score) && score > max.score
        ? { probability: risk[`${kind}RiskProbability`], impact: risk[`${kind}RiskImpact`], score }
        : max
    },
    { probability: { id: '', score: 0 }, impact: { id: '', score: 0 }, score: -1 }
  )

// Highest inherent/residual score across a request's (or supplier's) risks under the org's methodology.
// Single source of truth for the request grid's TPRM columns and the server CSV export — route both here
// rather than reimplementing. A -1 score (risks present, but no matrix cell for the level pair under
// MATRIX) is a deliberate sentinel the grid renders on; do not "correct" it to 0 or null.
export const getMaxRiskScoresInList = (
  risks: SummaryRiskScores[] | null | undefined,
  methodology: RiskScoreMethodology
): MaxRiskScore | null => {
  if (!risks || risks.length === 0) return null

  return {
    inherentRisk: getMaxScoreForKind(risks, 'inherent', methodology),
    residualRisk: getMaxScoreForKind(risks, 'residual', methodology),
  }
}
